import * as React from "react";
import { Input, InputProps } from "../input/Input";
import { InputGroupInput } from "../input";

export interface DebouncedInputProps extends Omit<InputProps, "onChange"> {
  onChange?: (value: string) => void;
  debounceMs?: number;
  useInputGroup?: boolean;
}

const DebouncedInput = React.forwardRef<HTMLInputElement, DebouncedInputProps>(
  ({ useInputGroup, onChange, debounceMs = 0, size, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(
      props.value || props.defaultValue || ""
    );
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Update internal value when external value changes
    React.useEffect(() => {
      if (props.value !== undefined) {
        setInternalValue(props.value);
      }
    }, [props.value]);

    const handleInputChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setInternalValue(newValue);

        // Clear existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set new timeout for debounced callback
        if (onChange) {
          if (debounceMs === 0) {
            // If no debounce, call immediately
            onChange(newValue);
          } else {
            timeoutRef.current = setTimeout(() => {
              onChange(newValue);
            }, debounceMs);
          }
        }
      },
      [onChange, debounceMs]
    );

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    if (useInputGroup) {
      return (
        <InputGroupInput {...props} value={internalValue} onChange={handleInputChange} ref={ref} />
      );
    }

    return (
      <Input {...props} size={size} value={internalValue} onChange={handleInputChange} ref={ref} />
    );
  }
);

DebouncedInput.displayName = "DebouncedInput";

export { DebouncedInput };
